import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FXAAPass } from 'three/examples/jsm/postprocessing/FXAAPass.js';

/**
 * GLSL (1.00 and 3.00: the OutputPass is a raw GLSL 1 shader, without isnan): replace NaN in `v`.rgb
 * with black (NaN is the one value unequal to itself) and clamp it to [0, max].
 */
const SANITIZE = (v: string, max: string) =>
  `if (!(${v}.r == ${v}.r && ${v}.g == ${v}.g && ${v}.b == ${v}.b)) ${v}.rgb = vec3(0.0);\n${v}.rgb = clamp(${v}.rgb, vec3(0.0), vec3(${max}));`;

/** Display-space finish: a gentle contrast curve and saturation, a vignette, and grain as dither. */
const GradeShader = {
  name: 'GradeShader',
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uVignette: { value: 0.22 },
    uSaturation: { value: 1.07 },
    uContrast: { value: 0.1 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uVignette;
    uniform float uSaturation;
    uniform float uContrast;
    varying vec2 vUv;
    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233)) + uTime) * 43758.5453);
    }
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      vec3 col = c.rgb;
      float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
      col = max(vec3(0.0), mix(vec3(l), col, uSaturation));
      // S-curve about mid-grey: a touch more punch without crushing the ends.
      col = clamp(col + uContrast * (col - 0.5) * (1.0 - abs(2.0 * col - 1.0)), 0.0, 1.0);
      vec2 d = vUv - 0.5;
      col *= 1.0 - uVignette * smoothstep(0.25, 0.85, dot(d, d) * 2.2);
      // ±0.5/255 of noise breaks up banding in the sky gradients.
      col += (hash(gl_FragCoord.xy) - 0.5) / 255.0;
      gl_FragColor = vec4(col, c.a);
    }`,
};

/**
 * Post-processing: the scene renders into a half-float target, multisampled (the canvas' own
 * antialiasing doesn't apply once a composer draws it); ground-truth ambient occlusion (three's
 * GTAOPass, at half resolution) darkens creases, corners and the ground around buildings; bloom (half
 * resolution) lets the sun, glints and signal lamps glow, thresholded against the current exposure so
 * only highlights bloom; the OutputPass applies tone mapping and sRGB; a grade pass finishes (contrast,
 * saturation, vignette, dither). FXAA stands in for multisampling on the low preset.
 */
export class PostFx {
  readonly composer: EffectComposer;
  readonly gtao: GTAOPass;
  /** AO buffers relative to the canvas size. */
  aoScale = 0.5;
  readonly bloom: UnrealBloomPass;
  private readonly grade: ShaderPass;
  private readonly fxaa: FXAAPass;
  /** MSAA samples wanted for the scene target (capped by the buffer size in setSize). */
  msaa = 4;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
  ) {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    this.composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(size.x, size.y, { type: THREE.HalfFloatType, samples: 4 }));
    this.composer.addPass(new RenderPass(scene, camera));
    this.gtao = new GTAOPass(scene, camera, size.x * this.aoScale, size.y * this.aoScale);
    this.gtao.output = GTAOPass.OUTPUT.Default;
    this.gtao.blendIntensity = 0.9;
    // Metres: a 1.5 m reach picks up wall bases, window reveals and building-street contact.
    this.gtao.updateGtaoMaterial({ radius: 1.5, distanceExponent: 1.5, thickness: 1.5, scale: 1.1, samples: 12, distanceFallOff: 1, screenSpaceRadius: false });
    this.gtao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 5, radiusExponent: 1, rings: 2, samples: 12 });
    // GTAO redraws the scene with its own normal material, which knows nothing of vertex-shader
    // animation: pedestrians would appear there frozen in their rest pose, as a dark static figure.
    // Objects marked userData.noAO are hidden for that pass (like points and lines already are).
    const g = this.gtao as unknown as { _overrideVisibility: () => void; _visibilityCache: THREE.Object3D[] };
    const hideLines = g._overrideVisibility.bind(this.gtao);
    g._overrideVisibility = () => {
      hideLines();
      scene.traverse((o) => {
        if (o.userData.noAO && o.visible) {
          o.visible = false;
          g._visibilityCache.push(o);
        }
      });
    };
    this.composer.addPass(this.gtao);
    this.bloom = new UnrealBloomPass(new THREE.Vector2(size.x, size.y), 0.32, 0.55, 8);
    // The sky's sun disc is ~60 000 before the rest of the sky is added: past half float's 65 504, so
    // it can store as infinity. Bloom would blur that over the screen and tone mapping turn it into
    // NaN (black frames whenever the sun or a hot glint is in view). Bloom takes a clamped copy.
    const hp = this.bloom.materialHighPassFilter;
    hp.fragmentShader = hp.fragmentShader.replace('vec4 texel = texture2D( tDiffuse, vUv );', `vec4 texel = texture2D( tDiffuse, vUv );
${SANITIZE('texel', '400.0')}`);
    hp.needsUpdate = true;
    this.composer.addPass(this.bloom);
    const output = new OutputPass();
    // …and the output never tone-maps infinity or NaN (ACES turns infinity into NaN: a black pixel).
    const om = output.material as THREE.ShaderMaterial;
    om.fragmentShader = om.fragmentShader.replace('gl_FragColor = texture2D( tDiffuse, vUv );', `gl_FragColor = texture2D( tDiffuse, vUv );
${SANITIZE('gl_FragColor', '30000.0')}`);
    om.needsUpdate = true;
    this.composer.addPass(output);
    this.fxaa = new FXAAPass();
    this.fxaa.enabled = false;
    this.composer.addPass(this.fxaa);
    this.grade = new ShaderPass(GradeShader);
    this.composer.addPass(this.grade);
  }

  /** Quality: MSAA (mid/high) or FXAA (low), and bloom on or off. */
  configure(o: { msaa: number; bloom: boolean }): void {
    this.msaa = o.msaa;
    this.fxaa.enabled = o.msaa === 0;
    this.bloom.enabled = o.bloom;
  }

  get ao(): boolean {
    return this.gtao.enabled;
  }

  set ao(on: boolean) {
    this.gtao.enabled = on;
  }

  render(): void {
    // Bloom only what's brighter than about twice display white at the current exposure.
    this.bloom.threshold = 2.2 / Math.max(0.05, this.renderer.toneMappingExposure);
    this.grade.uniforms.uTime.value = (performance.now() / 1000) % 100;
    this.composer.render();
  }

  setSize(w: number, h: number): void {
    const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    // Multisampled half-float buffers are big: fewer samples on very large drawing buffers.
    const px = size.x * size.y;
    const samples = this.msaa === 0 ? 0 : px > 8e6 ? 0 : px > 4.5e6 ? 2 : this.msaa;
    for (const rt of [this.composer.renderTarget1, this.composer.renderTarget2]) {
      if (rt.samples !== samples) {
        rt.samples = samples;
        rt.dispose();
      }
    }
    this.fxaa.enabled = samples === 0;
    this.composer.setSize(w, h);
    this.gtao.setSize(Math.round(size.x * this.aoScale), Math.round(size.y * this.aoScale));
  }
}
